import { type Address } from "viem"
import { CUSTOM_CHAIN_CURRENCY_CONFIGS } from "@/helpers/constants"
import dayjs from "dayjs"

enum AppChainProvider {
  Overtime = "overtime",
  SxBet = "sx",
  Azuro = "azuro",
}
interface UserAccountData {
  id?: string
  wallet_address: string
  refer_code?: string
  invite_code?: string
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_term?: string
  utm_content?: string
  joined_on?: string
  last_login_time?: string
  odds_preference?: "decimal" | "fractional" | "moneyline"
  signin_token?: string
  signed_msg_id?: string
  last_signin_at?: string
  login_toolkit?: "privy" | "rainbow"
  login_method?: "wallet" | "social"
  referralStatistics?: {
    betCount: string
    betVolume: string
    userCount: string
  }
  new_account?: boolean
  first_name?: string
  last_name?: string
  email_id?: string
  profile_image?: string
  is_verified?: boolean
  twitter?: {
    profilePictureUrl?: string
  }
}

interface PrivyUserData {
  did: string // Privy DID
  createdAt: Date // Timestamp
  linkedAccounts: string // JSON string
  email: string | null // JSON string
  phone: string | null // JSON string
  wallet: string // JSON string
  google: string | null // JSON string
  apple: string | null // JSON string
  twitter: string | null // JSON string
  discord: string | null // JSON string
  telegram: string | null // JSON string
  github: string | null // JSON string
  linkedin: string | null // JSON string
  spotify: string | null // JSON string
  instagram: string | null // JSON string
  tiktok: string | null // JSON string
  farcaster: string | null // JSON string
  delegatedWallets: string // JSON string
  mfaMethods: string // JSON string
  hasAcceptedTerms: boolean
  isGuest: boolean
  wallet_address: string
}

interface UserAccountRequest {
  wallet_address?: Address | undefined
  refer_code?: string
  invite_code?: string
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_term?: string
  utm_content?: string
  joined_on?: string
  last_login_time?: string
  odds_preference?: "decimal" | "fractional" | "moneyline"
  signin_token?: string
  signed_msg_id?: string
  last_signin_at?: string
  login_toolkit?: "privy" | "rainbow"
  login_method?: "wallet" | "social"
}

interface PrivyUserResponse {
  data: PrivyUserData | null
}

interface WalletAccount {
  type: string
  connectorType?: string
  walletClientType?: string
  chainType?: string
  address?: string
  displayName?: string
  profilePictureUrl?: string
  photoUrl?: string
  firstVerifiedAt?: string
  verifiedAt?: string
}

interface User {
  linkedAccounts?: WalletAccount[]
  twitter?: {
    profilePictureUrl?: string
  }
}

// Get the embedded or external wallet based on description
const getWalletAddress = (user: User): string | undefined => {
  const walletAccount = user.linkedAccounts?.find((account: WalletAccount) => {
    if (account.type !== "wallet") {
      return false
    }

    // Check for embedded wallet
    if (
      account.connectorType === "embedded" &&
      account.walletClientType === "privy" &&
      account.chainType === "ethereum"
    ) {
      return true
    }

    // Check for external wallet
    if (
      account.connectorType === "injected" &&
      account.walletClientType === "privy" &&
      account.chainType === "ethereum"
    ) {
      return true
    }

    return false
  })

  return walletAccount?.address?.toLowerCase()
}

// Get email from linked accounts in sequence
const getEmail = (user: User) => {
  const emailTypes = ["email", "google_oauth", "apple_oauth", "discord"]
  const account = user.linkedAccounts?.find(
    (acc: any) => emailTypes.includes(acc.type) && acc.address
  )
  return account?.address?.toLowerCase()
}

// Get name from linked accounts in sequence
const getName = (user: any) => {
  const nameAccounts = [
    "google",
    "twitter",
    "linkedin",
    "tiktok",
    "github",
    "telegram",
    "farcaster",
  ]
  for (const type of nameAccounts) {
    const account = user.linkedAccounts?.find(
      (acc: WalletAccount) => acc.type === type
    )
    if (account?.displayName) {
      return account.displayName.split(" ") as [string, string]
    }
  }
  return [null, null]
}

// Helper to get high-res Twitter image
function getHighResTwitterImage(
  url: string | null | undefined
): string | null | undefined {
  if (!url) {
    return url
  }
  return url.replace(/_normal|_bigger|_mini/, "")
}

// Get profile image from linked accounts
const getProfileImage = (user: User): string | null => {
  const imageAccounts = ["twitter", "telegram"]
  for (const type of imageAccounts) {
    const account = user.linkedAccounts?.find(
      (acc: WalletAccount) => acc.type === type
    )
    if (account?.profilePictureUrl || account?.photoUrl) {
      const url = account.profilePictureUrl || account.photoUrl
      return type === "twitter"
        ? getHighResTwitterImage(url) || null
        : url || null
    }
    if (user.twitter) {
      return getHighResTwitterImage(user.twitter?.profilePictureUrl) || null
    }
  }
  return null
}

// Check email verification status and get timestamps
interface EmailVerification {
  verify_email_triggered: string | null
  is_verified: boolean
}

const getEmailVerification = (user: User): EmailVerification => {
  const emailAccount = user.linkedAccounts?.find(
    (acc: WalletAccount) =>
      acc.type === "email" ||
      acc.type === "google_oauth" ||
      acc.type === "apple_oauth" ||
      acc.type === "discord"
  )
  return {
    verify_email_triggered: emailAccount?.firstVerifiedAt || null,
    is_verified: emailAccount?.verifiedAt ? true : false,
  }
}

function getChainName(chain: string): string {
  const chainMap = new Map([
    ["chiliz spicy testnet", "chiliz"],
    ["chiliz chain", "chiliz"],
    ["base", "base"],
    ["base sepolia", "base"],
  ])

  return chainMap.get(chain.trim().toLowerCase()) || "polygon"
}

function getTokenName(chain: string): string {
  const validTokens = new Set(["wchz", "weth"])
  return validTokens.has(chain.toLowerCase()) ? chain.toLowerCase() : "usdt"
}

function encodeBase64UrlSafe(obj) {
  const jsonString = JSON.stringify(obj)
  const utf8Bytes = new TextEncoder().encode(jsonString) // Convert to UTF-8 bytes
  const base64String = btoa(String.fromCharCode(...utf8Bytes)) // Convert bytes to base64
    .replace(/\+/g, "-") // Replace "+" with "-"
    .replace(/\//g, "_") // Replace "/" with "_"
    .replace(/=+$/, "") // Remove trailing "="

  return base64String
}

const formatToFixed = (value: string | number, digitsCount: number): string => {
  const numValue = parseFloat(String(value))

  if (isNaN(numValue)) {
    return String(value)
  }

  return numValue.toFixed(digitsCount)
}

const decimalToFraction = value => {
  if (value == parseInt(value)) {
    return `${parseInt(value)}/1`
  }

  const eps = 1.0e-4
  let h, h1, h2, k, k1, k2, a, x

  x = +value - 1
  a = Math.floor(x)
  h1 = 1
  k1 = 0
  h = a
  k = 1

  while (x - a > eps * k * k) {
    x = 1 / (x - a)
    a = Math.floor(x)
    h2 = h1
    h1 = h
    k2 = k1
    k1 = k
    h = h2 + a * h1
    k = k2 + a * k1
  }

  return h + "/" + k
}

const decimalToAmerican = (value: number) => {
  let multiplier = 100

  if (value < 2) {
    multiplier = -100

    return formatToFixed(multiplier / (value - 1), 0)
  }

  return `+${formatToFixed((value - 1) * multiplier, 0)}`
}

const formatViewValue = (oddsView: string, value: string | number) => {
  if (oddsView === "fractional") {
    return decimalToFraction(value)
  }

  if (oddsView === "moneyline") {
    return decimalToAmerican(+value).toString()
  }
  if (oddsView === "decimalWith3Decimals") {
    return formatToFixed(value, 3).toString()
  }
  if (oddsView === "decimalWith5Decimals") {
    return formatToFixed(value, 5).toString()
  }
  // default to decimal with 2 decimals
  return formatToFixed(value, 2).toString()
}

const toKFormat = (num: number): string => {
  const formatter = Intl.NumberFormat("en", {
    notation: "compact",
    maximumFractionDigits: 1,
  })
  return formatter.format(num)
}

/**
 * Converts any text to a URL-friendly slug
 *
 * @param text - The text to format (e.g., "TableTennis", "Table Tennis", "My Blog Post")
 * @returns A lowercase, hyphen-separated slug (e.g., "table-tennis", "my-blog-post")
 *
 * @example
 * ```javascript
 * formatTextToSlug("TableTennis")      // "table-tennis"
 * formatTextToSlug("Table Tennis")     // "table-tennis"
 * formatTextToSlug("My Blog Post")     // "my-blog-post"
 * formatTextToSlug("IceHockey")        // "ice-hockey"
 * formatTextToSlug("Soccer")           // "soccer"
 * formatTextToSlug("UFC - UFC Fight Night: Albazi vs. Taira
") // "ufc-ufc-fight-night-albazi-vs-taira"
 * ```
 *
 * @performance O(n) where n is the length of the input string
 */
function formatTextToSlug(text: string) {
  return text
    .toString() // Convert to string
    .toLowerCase() // Convert to lowercase
    .trim() // Remove leading/trailing whitespace
    .replace(/[^a-z0-9\s-]/g, "") // Remove special characters
    .replace(/\s+/g, "-") // Replace spaces with hyphens
    .replace(/-+/g, "-") // Replace multiple hyphens with single hyphen
}

// Convert slug to title case and replace hyphens with spaces
const formatSportName = (slug: string): string => {
  return slug
    .split("-")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ")
}

/**
 * Converts camelCase text to space-separated format
 *
 * @param text - The camelCase text to format (e.g., "TableTennis", "IceHockey")
 * @returns A space-separated string with proper spacing (e.g., "Table Tennis", "Ice Hockey")
 *
 * @example
 * ```javascript
 * formatCamelCaseToSpaces("TableTennis")    // "Table Tennis"
 * formatCamelCaseToSpaces("IceHockey")      // "Ice Hockey"
 * formatCamelCaseToSpaces("Basketball")     // "Basketball"
 * formatCamelCaseToSpaces("NFL")            // "N F L"
 * ```
 *
 * @performance O(n) where n is the length of the input string
 */
const formatCamelCaseToSpaces = (text: string): string => {
  if (text === "eSports") {
    return "Esports"
  }
  return text.replace(/([A-Z])/g, " $1").trim()
}

const formatTime = (timestamp: number | string) => {
  return dayjs(+timestamp * 1000).format("HH:mm")
}

const getSelectedTokenSymbol = (chainId: number): string => {
  return (
    CUSTOM_CHAIN_CURRENCY_CONFIGS.find(chain => chain.id === chainId)
      ?.currency || ""
  )
}

function toTitleCase(str: string) {
  return str
    .replace(/[-_]+/g, " ") // replace - and _ with spaces
    .replace(
      /\w\S*/g,
      word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ) // capitalize each word
}

function isAfter48Hours(gameTime: string) {
  const now = new Date()
  const after48Hours = new Date(now.getTime() + 48 * 60 * 60 * 1000) // add 48 hours in ms
  return new Date(gameTime).getTime() > after48Hours.getTime()
}

function hasGameStarted(gameTime: string) {
  const now = new Date()
  return new Date(gameTime).getTime() <= now.getTime()
}

function slugifyLabel(label: string): string {
  return label
    .toLowerCase()
    .replace(/\s*-\s*/g, "---")
    .replace(/\s+/g, "-")
    .split("")
    .map(ch => {
      if (/[a-z0-9-]/.test(ch)) {
        return ch
      }
      return encodeURIComponent(ch)
    })
    .join("")
}

function validateSlug(label: string, slug: string): boolean {
  return slugifyLabel(label) === slug
}

export {
  getWalletAddress,
  getEmail,
  getName,
  getProfileImage,
  getEmailVerification,
  getTokenName,
  getChainName,
  encodeBase64UrlSafe,
  formatViewValue,
  decimalToFraction,
  decimalToAmerican,
  toKFormat,
  getHighResTwitterImage,
  formatTextToSlug,
  formatCamelCaseToSpaces,
  formatTime,
  formatSportName,
  getSelectedTokenSymbol,
  toTitleCase,
  isAfter48Hours,
  hasGameStarted,
  validateSlug,
}

export type {
  UserAccountData,
  PrivyUserData,
  PrivyUserResponse,
  UserAccountRequest,
}
